import { analytics } from '@/lib/analytics';
import { api } from '@/lib/api';
import {
  isServerSessionValidated,
  markServerSessionValidated,
  resetServerSessionValidation,
} from '@/lib/auth-session-state';
import { ORG_CHOICE_SESSION_KEY } from '@/lib/org-choice-session';
import { mapApiUserProfileToStore, type ApiUserProfile } from '@/lib/map-api-user-profile';
import { getBearerTokenSync } from '@/lib/auth-token';
import {
  getRefreshBrowserSessionPromise,
  isRefreshBrowserSessionInFlight,
  refreshBrowserSession,
} from '@/lib/session-refresh';
import { useAuth } from '@/stores/use-auth';

let restoreSessionInFlight: Promise<boolean> | null = null;
/** >0 while the current call stack is inside `restoreSession()` (avoids 401-handler deadlock). */
let restoreSessionCallDepth = 0;

/** True while `restoreSession()` is in flight (e.g. Sign-In "Restoring session…"). */
export function isRestoreSessionActive(): boolean {
  return restoreSessionInFlight !== null;
}

/** True while refresh or restore is in flight (parallel 401 handlers should wait). */
export function isSessionRecoveryInFlight(): boolean {
  return isRestoreSessionActive() || isRefreshBrowserSessionInFlight();
}

/** Await in-flight refresh/restore started by another request or tab. */
export async function waitForSessionRecovery(): Promise<void> {
  const refreshPromise = getRefreshBrowserSessionPromise();
  if (refreshPromise) {
    await refreshPromise.catch(() => false);
  }
  if (restoreSessionInFlight && restoreSessionCallDepth === 0) {
    await restoreSessionInFlight.catch(() => false);
  }
}

export async function restoreSession(): Promise<boolean> {
  if (isServerSessionValidated()) {
    if (!useAuth.getState().isAuthenticated) {
      resetServerSessionValidation();
    } else {
      return true;
    }
  }

  if (restoreSessionInFlight) {
    return restoreSessionInFlight;
  }

  restoreSessionInFlight = (async () => {
    restoreSessionCallDepth += 1;
    try {
      const needsBootstrapRefresh = !useAuth.getState().isAuthenticated || !getBearerTokenSync();
      if (needsBootstrapRefresh) {
        await refreshBrowserSession({
          trigger: 'reactive',
          force: true,
          allowBootstrap: true,
        });
      }

      const tokenBefore = useAuth.getState().token;
      const apiUser = await api.get<ApiUserProfile & { needs_org_choice?: boolean }>('/users/me', {
        _skipNestedSessionRestore: true,
      });
      const tokenAfter = useAuth.getState().token;

      if (apiUser.needs_org_choice) {
        sessionStorage.setItem(ORG_CHOICE_SESSION_KEY, '1');
      }

      // Keep Bearer when refresh updated it, or when a valid token remains in store/storage.
      const preserveToken =
        tokenAfter !== tokenBefore || Boolean(tokenAfter) || Boolean(getBearerTokenSync());
      useAuth
        .getState()
        .setUser(
          mapApiUserProfileToStore(apiUser),
          preserveToken ? (tokenAfter ?? getBearerTokenSync() ?? undefined) : null
        );
      markServerSessionValidated();
      analytics.capture('auth_session_restored', {
        needs_org_choice: Boolean(apiUser.needs_org_choice),
        has_organization: Boolean(apiUser.organization),
      });
      return true;
    } catch {
      if (useAuth.getState().isAuthenticated || getBearerTokenSync()) {
        useAuth.getState().logout();
      }
      return false;
    } finally {
      restoreSessionCallDepth -= 1;
      restoreSessionInFlight = null;
    }
  })();

  return restoreSessionInFlight;
}
